import { useState, useEffect, useRef } from "react"
import { useQueryState } from "nuqs"
import { Link } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCircleInfo,
  faSearch,
  faTimes,
  faEye,
  faQrcode,
  faComments,
  faChartLine,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons"
import Dropdown from "../../components/Dropdown"
import DateRangeControl from "../../components/DateRangeControl"
import ApiError from "../../components/ApiError"
import Loader from "../../components/Loader"
import {
  useVideoScanAnalytics,
  useVideoScanFilterOptions,
} from "../../hooks/useVideoLogs"
import {
  useDateRangeFilter,
  CUSTOM_RANGE,
} from "../../hooks/useDateRangeFilter"
import { formatISODate } from "../../utils/datetime"
import routePaths from "../../constants/path"
import FeedbackDrawer from "./FeedbackDrawer"

const ALL = "All"
const DEFAULT_RANGE = "This Month"

const sortOptions = Object.freeze({
  "Last scan": "LastScanAt:DESC",
  "First scan": "FirstScanAt:DESC",
  "Most scans": "TotalScans:DESC",
  "Most unique scans": "UniqueScans:DESC",
})

const tileAccents = {
  blue: "bg-blue-50 text-blue-600",
  indigo: "bg-indigo-50 text-indigo-600",
  green: "bg-green-50 text-green-600",
  amber: "bg-amber-50 text-amber-600",
}

const StatTile = ({ label, value, hint, icon, accent = "blue", to }) => {
  const Wrapper = to ? Link : "div"
  return (
    <Wrapper
      className={`group relative flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3 flex-1 min-w-[170px] shadow-sm transition-shadow hover:shadow-md ${
        to ? "cursor-pointer hover:border-blue-300" : ""
      }`}
      title={to ? "View in Customer Feedback" : hint}
      {...(to ? { to } : {})}
    >
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${tileAccents[accent]}`}
      >
        <FontAwesomeIcon className="w-4 h-4" icon={icon} />
      </div>
      <div className="min-w-0">
        <div className="text-2xl font-bold text-gray-900 leading-none tabular-nums">
          {(value ?? 0).toLocaleString()}
        </div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mt-1 truncate">
          {label}
        </div>
      </div>
      {to && (
        <FontAwesomeIcon
          className="absolute right-3 top-3 h-3 w-3 text-gray-300 opacity-0 transition-opacity group-hover:opacity-100"
          icon={faArrowRight}
        />
      )}
    </Wrapper>
  )
}

const Cell = ({ children, className = "" }) => (
  <td className={`px-4 py-2.5 text-sm text-gray-700 ${className}`}>
    {children}
  </td>
)

const columns = [
  { header: "Accession" },
  { header: "Issuer" },
  { header: "Body part" },
  { header: "Total", align: "text-right" },
  { header: "Unique", align: "text-right" },
  { header: "First scan" },
  { header: "Last scan" },
  { header: "Feedback" },
]

const VideoScanAnalytics = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const [activeSearchTerm, setActiveSearchTerm] = useQueryState("search", {
    defaultValue: "",
  })
  const {
    dateRange,
    customFrom,
    customTo,
    isCustomRange,
    start,
    end,
    triggerLabel,
    presetOptions,
    getCustomSeed,
    handleSelectPreset,
    handleApplyCustom,
    handleResetFilters,
  } = useDateRangeFilter({ defaultRange: DEFAULT_RANGE })
  const [issuer, setIssuer] = useQueryState("issuer", {
    defaultValue: ALL,
  })
  const [bodyPart, setBodyPart] = useQueryState("bodyPart", {
    defaultValue: ALL,
  })
  const [sortLabel, setSortLabel] = useQueryState("sort", {
    defaultValue: "Last scan",
  })

  // The report whose feedback is open in the drawer.
  const [selectedReport, setSelectedReport] = useState(null)

  const sentinelRef = useRef(null)

  const { data: filterOptions } = useVideoScanFilterOptions()

  const timePeriod = start || end ? [start, end].join(",") : undefined

  const filters = {
    searchTerm: activeSearchTerm?.trim() || undefined,
    timePeriod,
    patientIssuer: issuer === ALL ? undefined : issuer,
    bodyPart: bodyPart === ALL ? undefined : bodyPart,
  }

  const sortBy = sortOptions[sortLabel] || sortOptions["Last scan"]

  const {
    data: { data, total, totals } = { data: [], total: 0, totals: {} },
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useVideoScanAnalytics({ limit: 20, sortBy, filters })

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { rootMargin: "200px", threshold: 0.1 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // Keep the input in step with a search cleared from elsewhere (a back
  // navigation, a shared link without the param).
  useEffect(() => {
    setSearchTerm(activeSearchTerm || "")
  }, [activeSearchTerm])

  const handleClearFilters = () => {
    handleResetFilters()
    setIssuer(ALL)
    setBodyPart(ALL)
  }

  const handleClearSearch = () => {
    setSearchTerm("")
    setActiveSearchTerm("")
  }

  const hasActiveFilters =
    dateRange !== DEFAULT_RANGE || issuer !== ALL || bodyPart !== ALL

  // Carries this page's date range over so "Feedback submitted" opens
  // Customer Feedback already scoped to the same period, not "This Month".
  // Issuer/body part/sort aren't filters Customer Feedback has, so they
  // don't carry over.
  const feedbackLinkParams = new URLSearchParams({ dateRange })
  if (isCustomRange) {
    if (customFrom) feedbackLinkParams.set("from", customFrom)
    if (customTo) feedbackLinkParams.set("to", customTo)
  }
  const feedbackLink = `${routePaths.customerFeedback}?${feedbackLinkParams.toString()}`

  if (isError) {
    return (
      <div className="mt-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  return (
    <div className="flex min-h-[calc(100vh_-_80px)] flex-col p-4">
      <div className="mb-4 shrink-0">
        <h1 className="text-2xl font-bold text-gray-900">QR Scan Analytics</h1>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 mb-3 shrink-0">
        <div className="flex flex-wrap items-end gap-3">
          <DateRangeControl
            customLabel={CUSTOM_RANGE}
            getCustomSeed={getCustomSeed}
            presets={presetOptions}
            title="Filter by when the report was scanned"
            triggerLabel={triggerLabel}
            value={dateRange}
            onApplyCustom={handleApplyCustom}
            onSelectPreset={handleSelectPreset}
          />

          <Dropdown
            label="ISSUER"
            options={[ALL, ...(filterOptions?.patientIssuers || [])]}
            setValue={setIssuer}
            title="Filter by issuer"
            value={issuer}
          />

          <Dropdown
            label="BODY PART"
            options={[ALL, ...(filterOptions?.bodyParts || [])]}
            setValue={setBodyPart}
            title="Filter by report body part"
            value={bodyPart}
          />

          <Dropdown
            label="SORT BY"
            options={Object.keys(sortOptions)}
            setValue={setSortLabel}
            title="Sort the reports"
            value={sortLabel}
          />

          <button
            className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={!hasActiveFilters}
            title="Reset filters to their defaults"
            onClick={handleClearFilters}
          >
            Reset
          </button>

          <div className="flex-1" />

          {/* Search — Enter runs it, the cross clears it */}
          <div>
            <label className="block text-xs font-medium mb-1 ml-1 text-gray-700">
              ACCESSION NUMBER
            </label>
            <div className="relative">
              <FontAwesomeIcon
                className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none"
                icon={faSearch}
              />
              <input
                className="w-full min-w-[300px] pl-9 pr-9 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search and press Enter"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    setActiveSearchTerm(searchTerm)
                  }
                }}
              />
              {searchTerm && (
                <button
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  title="Clear search"
                  type="button"
                  onClick={handleClearSearch}
                >
                  <FontAwesomeIcon className="w-3 h-3" icon={faTimes} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Totals across the whole filtered set */}
      <div className="flex flex-wrap gap-3 mb-3 shrink-0">
        <StatTile
          accent="blue"
          hint="Reports with at least one tracked event in this period"
          icon={faQrcode}
          label="Reports scanned"
          value={totals.ReportsScanned}
        />
        <StatTile
          accent="indigo"
          hint="Every QR page open, admin previews excluded"
          icon={faChartLine}
          label="Total scans"
          value={totals.TotalScans}
        />
        <StatTile
          accent="indigo"
          hint="Distinct devices per report"
          icon={faEye}
          label="Unique scans"
          value={totals.UniqueScans}
        />
        <StatTile
          accent="green"
          hint={`Feedback responses submitted, across ${totals.ReportsWithFeedback ?? 0} report(s)`}
          icon={faComments}
          label="Feedback submitted"
          to={feedbackLink}
          value={totals.FeedbackSubmitted}
        />
      </div>

      {totals.UnattributedScans > 0 && (
        <div className="flex items-start gap-2 text-xs text-gray-600 bg-amber-50/60 border border-amber-200 rounded-lg px-3 py-2 mb-3 shrink-0">
          <FontAwesomeIcon
            className="w-3 h-3 mt-0.5 shrink-0 text-amber-500"
            icon={faCircleInfo}
          />
          <span>
            {totals.UnattributedScans.toLocaleString()} scan
            {totals.UnattributedScans !== 1 ? "s" : ""} came from browsers with
            storage blocked (private mode) and are counted in the total but not
            in unique scans.
          </span>
        </div>
      )}

      {/* Results */}
      <div className="flex min-h-0 flex-1 flex-col">
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center gap-4">
            <Loader />
            <span className="text-gray-600">Loading scan analytics...</span>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex justify-between items-center mb-2 px-1 shrink-0">
              <span className="text-xs text-gray-500">
                Showing <span className="font-bold">{data.length}</span> of{" "}
                <span className="font-bold">{total}</span> reports
              </span>
              {activeSearchTerm && (
                <span className="text-xs text-gray-500">
                  Filtered by accession{" "}
                  <span className="font-semibold text-gray-700">
                    {activeSearchTerm}
                  </span>
                </span>
              )}
            </div>

            {data.length > 0 ? (
              <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      {columns.map(({ header, align = "text-left" }) => (
                        <th
                          key={header}
                          className={`px-4 py-2.5 ${align} text-[10px] font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap border-b border-gray-200`}
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.map((row) => (
                      <tr
                        key={row.ReportVideosUuid}
                        className="hover:bg-blue-50/40 transition-colors"
                      >
                        <Cell className="font-semibold text-gray-900 whitespace-nowrap">
                          <a
                            className="hover:text-blue-600 hover:underline"
                            href={`${window.location.origin}/educational-videos/${row.ReportVideosUuid}?source=preview`}
                            rel="noopener noreferrer"
                            target="_blank"
                            title="Open the patient page (flagged as an internal preview)"
                          >
                            {row.AccessionNo || "—"}
                          </a>
                        </Cell>
                        <Cell>{row.PatientIssuer || "—"}</Cell>
                        <Cell>
                          {row.BodyPart ? (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-blue-50 border border-blue-200 text-blue-700 font-medium">
                              {row.BodyPart.toUpperCase()}
                            </span>
                          ) : (
                            "—"
                          )}
                        </Cell>
                        <Cell className="text-right font-bold tabular-nums">
                          {row.TotalScans}
                        </Cell>
                        <Cell className="text-right font-bold tabular-nums">
                          {row.UniqueScans}
                        </Cell>
                        <Cell className="whitespace-nowrap text-xs text-gray-500">
                          {formatISODate(row.FirstScanAt) || "—"}
                        </Cell>
                        <Cell className="whitespace-nowrap text-xs text-gray-500">
                          {formatISODate(row.LastScanAt) || "—"}
                        </Cell>
                        <Cell className="whitespace-nowrap">
                          {row.FeedbackSubmittedCount > 0 ? (
                            <button
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-green-50 border border-green-200 text-green-700 hover:bg-green-100 hover:border-green-300 transition-colors"
                              title={`View ${row.FeedbackSubmittedCount} feedback response(s)`}
                              onClick={() => setSelectedReport(row)}
                            >
                              <FontAwesomeIcon
                                className="w-3 h-3"
                                icon={faEye}
                              />
                              View
                              {row.FeedbackSubmittedCount > 1
                                ? ` (${row.FeedbackSubmittedCount})`
                                : ""}
                            </button>
                          ) : row.FeedbackStartedCount > 0 ? (
                            <span
                              className="px-2 py-0.5 text-xs rounded-full bg-amber-50 border border-amber-300 text-amber-800"
                              title="The form was started but never submitted"
                            >
                              abandoned
                              {row.FeedbackStartedCount > 1
                                ? ` ×${row.FeedbackStartedCount}`
                                : ""}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </Cell>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Infinite Scroll Sentinel */}
                <div ref={sentinelRef} className="h-1" />

                {hasNextPage && (
                  <div className="flex justify-center py-4 border-t border-gray-100">
                    {isFetchingNextPage ? (
                      <div className="flex items-center gap-2">
                        <Loader />
                        <span className="text-gray-600">
                          Loading more records...
                        </span>
                      </div>
                    ) : (
                      <button
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition-colors text-sm font-medium"
                        onClick={fetchNextPage}
                      >
                        Load More
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-white border border-gray-200 rounded-xl">
                <FontAwesomeIcon
                  className="w-8 h-8 text-gray-300"
                  icon={faQrcode}
                />
                <p className="text-sm text-gray-500">
                  No scans recorded for these filters.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <FeedbackDrawer
        report={selectedReport}
        onClose={() => setSelectedReport(null)}
      />
    </div>
  )
}

export default VideoScanAnalytics
